import ColoredChipsCell from "@common/components/coloredChipsCell/ColoredChipsCell";
import { DotLegalEmptyState, DotLegalLink, DotLegalPaper, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "@localization/useTranslation";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import EmptyState from "@common/svgs/emptyStates/emptyState_1.svg?react";
import SystemIcon from "../../../system/systemIcon/SystemIcon";
import SystemStatusChip from "../../../system/systemOverview/SystemStatusChip";
import useProcessingActivityAssetsTab, { ProcessingActivityAssetTableRow } from "./ProcessingActivityAssetsTab.hooks";

export interface IProcessingActivityAssetsTab {
    processingActivityId: string;
}

function ProcessingActivityAssetsTab(props: IProcessingActivityAssetsTab) {
    const { translateString } = useTranslation();
    const {
        isLoading,
        assets,
        hasData,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        canOpenAsset,
        getAssetUrl,
        openAsset,
    } = useProcessingActivityAssetsTab(props);

    function getHeaders() {
        const headers = Array<ITableHeader<ProcessingActivityAssetTableRow>>();
        headers.push({ property: null, text: "", align: "left", showOnMobile: true, notSortable: true });
        headers.push({ property: "name", text: translateString("asset"), align: "left", showOnMobile: true });
        headers.push({ property: "type", text: translateString("type"), align: "left", showOnMobile: true });
        headers.push({ property: "businessAreaNames", text: translateString("businessAreas"), align: "left", showOnMobile: true });
        headers.push({ property: "responsible", text: translateString("responsible"), align: "left", showOnMobile: true });
        headers.push({ property: "status", text: translateString("status"), align: "left", showOnMobile: true });
        return headers;
    }

    return (
        <DotLegalPaper>
            {hasData ? (
                <DotLegalTableWithFilterBar
                    headers={getHeaders()}
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    clickableRows={canOpenAsset}
                    data={assets}
                    columnsExcludedFromFilter={["businessAreas"]}
                    isLoading={isLoading}
                    filterBar={{
                        isLoading: isLoading,
                        filterData: getFilterOptions(),
                        fetchFilterData: true,
                        onMenuClose: onFilterChange,
                        onRemove: onFilterChange,
                        pinnedFilterIds: pinnedFilterIds,
                        onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                    }}
                    emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noData")}
                    renderRow={(row) => (
                        <TableRow
                            key={row.id}
                            onClick={canOpenAsset ? () => openAsset(row.id) : undefined}
                            sx={canOpenAsset ? { cursor: "pointer" } : undefined}
                        >
                            <TableCell width={75}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <SystemIcon systemName={row.name} size="small" />
                                </Box>
                            </TableCell>
                            <TableCell>
                                {canOpenAsset ? (
                                    <DotLegalLink linkColor={"primary"} bold to={getAssetUrl(row.id)}>
                                        {row.name}
                                    </DotLegalLink>
                                ) : (
                                    row.name
                                )}
                            </TableCell>
                            <TableCell>{row.type}</TableCell>
                            <TableCell>
                                <ColoredChipsCell items={row.businessAreas} />
                            </TableCell>
                            <TableCell>{row.responsible}</TableCell>
                            <TableCell>
                                <SystemStatusChip status={row.statusId} value={row.status} />
                            </TableCell>
                        </TableRow>
                    )}
                    paginationLabelOf={""}
                    rowsPerPage={30}
                />
            ) : (
                <DotLegalEmptyState icon={<EmptyState />} text={translateString("noAssetsOnProcessingActivity")} />
            )}
        </DotLegalPaper>
    );
}

export default ProcessingActivityAssetsTab;
